import { getDefaultColumnsSettings, getAbrWorldMapColumnsSettings } from '../TableColumnsSettings';

import { generateZoomInitialTables } from '../../../util/TableTools';

import {
   METRIC_BUFFER_RATIO,
   METRIC_PI,
   PI_BOTH,
   PI_INGESTS,
   PI_PLAYOUTS,
   METRIC_LATENCY,
   METRIC_PLAYTIME,
   METRIC_USAGE,
   METRIC_ABR_PLAYTIME,
   METRIC_QOE,
   METRIC_ABR_VIEWER,
   METRIC_ABR_SWITCHES,
} from '../../widgets/world/WorldMapConstants';

import {
   format_time_Number,
   getIntegerWithDecimalSeparator,
   roundUsageWith5Digits,
} from '../../../util/NumberFormatter';
import { GREEN_TO_RED_10_STEPS_HSL } from '../../../constants/colors';
import { getSwitchLabel } from '../../widgets/world/DataCircle/ContentTypes/AbrSubComponents/AbrSwitchesLabels';
import numeral from 'numeral';
import { BINTU_ACCOUNTING, METRICS_PLAYER } from '../ZoomConstants';
import { STREAM } from '../../../constants/dataGroup';

const qoeCellStyleFn = (cell) => {
   const cellValue = isNaN(cell) ? 9 : cell;
   const valueRangeMoved = cellValue === 0 ? 0 : cellValue - 1;
   const colorIdx = 9 - Math.floor(valueRangeMoved / 10);
   const [h, s, l] = GREEN_TO_RED_10_STEPS_HSL[colorIdx];
   const opacity = 0.3;
   return {
      verticalAlign: 'middle',
      textAlign: 'center',
      backgroundColor: `hsla(${h}, ${s}%, ${l}%,${opacity})`,
   };
};

export const WIDGET_SETTINGS = {
   [METRIC_PI]: {
      title: (country) => `${country} Playouts / Ingests`,
      exIndexName: BINTU_ACCOUNTING,
      initialTable: generateZoomInitialTables(['playouts', 'ingests']),
      path: 'world/playoutIngest',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data,
      categories: {
         labels: ['Playout', 'Ingest'],
         propNames: ['playouts', 'ingests'],
      },
      columnNames: ['Counts'],
      columnsSettings: getDefaultColumnsSettings,
      subSwitchInitialIdxMapping: { [PI_BOTH]: 0, [PI_PLAYOUTS]: 0, [PI_INGESTS]: 1 },
      dataFieldNames: ['doc_count'],
      formatters: [(cell) => getIntegerWithDecimalSeparator(cell)],
      isWorldmap: true,
   },
   [METRIC_BUFFER_RATIO]: {
      title: (country) => `${country} Buffering ratio`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['buffering']),
      path: 'world/bufferingratio',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data,
      categories: {
         labels: ['Buffering ratio'],
         propNames: ['buffering'],
      },
      columnNames: ['Buffering ratio [%]', 'Playtime'],
      columnsSettings: getDefaultColumnsSettings,
      dataFieldNames: ['bufferRatio', 'playtime'],
      formatters: [(cell) => cell.toFixed(2), (cell) => format_time_Number(cell)],
      isWorldmap: true,
   },
   [METRIC_LATENCY]: {
      title: (country) => `${country} Latency`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['latency']),
      path: 'world/latency',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data,
      categories: {
         labels: ['Latency'],
         propNames: ['latency'],
      },
      columnNames: ['Latency [s]', 'Playtime', 'Unique plays'],
      columnsSettings: getDefaultColumnsSettings,
      dataFieldNames: ['latency', 'playtime', 'uniquePlays'],
      formatters: [
         (cell) => cell.toFixed(2),
         (cell) => format_time_Number(cell),
         (cell) => numeral(cell).format('0,0'),
      ],
      isWorldmap: true,
   },
   [METRIC_PLAYTIME]: {
      title: (country) => `${country} Average playtime`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['playtime']),
      path: 'world/playtime',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data,
      categories: {
         labels: ['Avg. playtime'],
         propNames: ['playtime'],
      },
      columnNames: ['Avg. playtime', 'Playtime', 'Unique plays'],
      columnsSettings: getDefaultColumnsSettings,
      dataFieldNames: ['avgPlaytime', 'playtime', 'uniquePlays'],
      formatters: [
         (cell) => format_time_Number(cell),
         (cell) => format_time_Number(cell),
         (cell) => numeral(cell).format('0,0'),
      ],
      isWorldmap: true,
   },
   [METRIC_USAGE]: {
      title: (country) => `${country} Usage playout / ingest`,
      exIndexName: BINTU_ACCOUNTING,
      initialTable: generateZoomInitialTables(['playoutBytes', 'ingestBytes']),
      path: 'world/usage',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data,
      categories: {
         labels: ['Playout', 'Ingest'],
         propNames: ['playoutBytes', 'ingestBytes'],
      },
      columnNames: ['Usage'],
      columnsSettings: getDefaultColumnsSettings,
      dataFieldNames: ['value'],
      formatters: [(cell) => roundUsageWith5Digits(cell)],
      isWorldmap: true,
   },
   [METRIC_ABR_PLAYTIME]: {
      title: (country) => `${country} Playtime per device type / video height ABR profile`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['mobile', 'desktop']),
      path: 'world/abr/playtime',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data,
      categories: {
         labels: ['Mobile', 'Desktop'],
         propNames: ['mobile', 'desktop'],
      },
      columnNames: [''],
      columnsSettings: getAbrWorldMapColumnsSettings,
      formatters: [(cell) => format_time_Number(cell)],
      isWorldmap: true,
   },
   [METRIC_ABR_VIEWER]: {
      title: (country) => `${country} Viewers per video height ABR profile`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['viewer']),
      path: 'world/abr/viewer',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data.result,
      categories: {
         labels: ['Viewers'],
         propNames: ['viewer'],
      },
      columnNames: [''],
      columnsSettings: getAbrWorldMapColumnsSettings,
      formatters: [(cell) => getIntegerWithDecimalSeparator(cell)],
      isWorldmap: true,
   },
   [METRIC_ABR_SWITCHES]: {
      title: (country) => `${country} Switches between ABR profiles`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['high->mid', 'mid->low', 'low->mid', 'mid->high'], {
         orga: '-',
         doc_count: 0,
      }),
      path: 'world/abr/switches',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data.result,
      categories: {
         labels: [
            getSwitchLabel('high->mid', true),
            getSwitchLabel('mid->low', true),
            getSwitchLabel('low->mid', true),
            getSwitchLabel('mid->high', true),
         ],
         propNames: ['high->mid', 'mid->low', 'low->mid', 'mid->high'],
      },
      columnNames: ['Switches'],
      columnsSettings: getDefaultColumnsSettings,
      dataFieldNames: ['doc_count'],
      formatters: [(cell) => getIntegerWithDecimalSeparator(cell)],
      isWorldmap: true,
   },
   [METRIC_QOE]: {
      title: (country) => `${country} QoE score`,
      exIndexName: METRICS_PLAYER,
      initialTable: generateZoomInitialTables(['qoe']),
      path: 'world/qoe',
      urlAddon: (country, cdn) => `&zoomMetric=${country}${cdn}`,
      dataAccessFn: (data) => data,
      categories: {
         labels: ['QoE score'],
         propNames: ['qoe'],
      },
      columnNames: ['QoE Score', 'Unique plays'],
      columnsSettings: getDefaultColumnsSettings,
      dataFieldNames: ['score', 'uniquePlays'],
      formatters: [(cell) => cell, (cell) => numeral(cell).format('0,0')],
      isWorldmap: true,
      shownTerms: [STREAM],
      cellStyles: [qoeCellStyleFn, undefined],
   },
};
